import { useEffect, useState } from "react";
import { getDiagnostics } from "@/lib/api";
import type { DiagnosticsLog } from "@lib/diagnostics";

const POLL_MS = 1500;

/**
 * Polls the dev-only diagnostics endpoint for a video while local dev
 * (`import.meta.env.DEV`) is active. In production builds and when the video id
 * is unavailable (e.g. the home page) the hook stays silent.
 */
export function useDiagnostics(videoId: string | null) {
  const [log, setLog] = useState<DiagnosticsLog>({ startedAt: 0, events: [] });
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!import.meta.env.DEV || !videoId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const next = await getDiagnostics(videoId);
        if (cancelled) return;
        setLog(next);
        if (next.startedAt > 0) setAvailable(true);
      } catch {
        /* the endpoint is 404 outside local dev; keep whatever we have */
      }
    };

    void poll();
    const timer = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [videoId]);

  return { enabled: import.meta.env.DEV, available, ...log };
}